import { useState } from 'react';
import { useCachedImage } from '@/shared/hooks/useCachedImage';
import { C } from '@/shared/ui/theme/tokens';

interface AvatarProps {
  initials: string;
  color: string;
  size?: number;
  photoUrl?: string | null;
  /**
   * Optional custom font size for the letter avatar (in px).
   * If not provided, defaults to `size * 0.35`.
   * Used by TimelogDetailModal to render 100×100 avatar with 25.2px letters.
   */
  letterFontSize?: number;
}

export function Avatar({ initials, color, size = 44, photoUrl, letterFontSize }: AvatarProps) {
  // L1 blob cache: Dexie → object URL. Null on offline miss (Avatar falls back to initials).
  const { src } = useCachedImage(photoUrl);

  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Prev-prop-during-render pattern: synchronous bailout when photoUrl changes.
  // Keyed on photoUrl (the original prop), not src (the async blob URL), so the
  // reset is immediate and synchronous even before useCachedImage resolves.
  // State updates during render are applied in the same pass (no extra commit),
  // so the consumer never sees a stale imageError/imageLoaded from the old URL.
  const [prevUrl, setPrevUrl] = useState(photoUrl);
  if (prevUrl !== photoUrl) {
    setPrevUrl(photoUrl);
    setImageError(false);
    setImageLoaded(false);
  }

  // src is null while loading or on offline miss; Avatar shows initials in those cases.
  const hasValidPhoto = src !== null && !imageError;

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: letterFontSize !== undefined ? letterFontSize : size * 0.35,
        fontWeight: 700,
        color: '#fff',
        letterSpacing: '0.02em',
        overflow: 'hidden',
        position: 'relative',
        boxShadow: `0 0 0 1.5px ${C.blue}`,
      }}
    >
      {hasValidPhoto ? (
        <>
          {/* Image source is a blob: URL produced by useCachedImage (Dexie L1 cache).
              blob: URLs are same-origin by spec and bypass the Service Worker interceptor,
              making this deterministic and quota-independent.
              crossOrigin="anonymous" is retained for any future direct-URL fallback paths
              and COEP compatibility (Access-Control-Allow-Origin: * + require-corp). */}
          <img
            src={src}
            alt=""
            crossOrigin="anonymous"
            onError={() => {
              setImageError(true);
              setImageLoaded(false);
            }}
            onLoad={() => setImageLoaded(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.4s ease-out',
            }}
          />

          {/* Glassmorphism overlay + spinner (shown while loading) */}
          {!imageLoaded && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'avatarFadeOut 0.4s ease-out forwards',
                animationDelay: imageLoaded ? '0s' : '9999s', // Only fade out when loaded
              }}
            >
              {/* Spinner */}
              <div
                style={{
                  width: size * 0.35,
                  height: size * 0.35,
                  border: `${Math.max(2, size * 0.06)}px solid rgba(255, 255, 255, 0.3)`,
                  borderTopColor: '#fff',
                  borderRadius: '50%',
                  animation: 'avatarSpin 0.8s linear infinite',
                }}
              />
            </div>
          )}
        </>
      ) : (
        initials
      )}

      <style>{`
        @keyframes avatarSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes avatarFadeOut {
          from { opacity: 1; }
          to { opacity: 0; visibility: hidden; }
        }
      `}</style>
    </div>
  );
}
